import { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { ClientLayout } from "../components/ClientLayout";
import { clientApi } from "../services/clientApi.service";
import { buildReplayFromOrders } from "../utils/orderReplay";

/**
 * Page de retour après Stripe Checkout (succès ou annulation).
 */
export function ClientCheckoutResultPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const status = searchParams.get("status");
  const sessionId = searchParams.get("session_id");

  const [loading, setLoading] = useState(
    (status === "success" && Boolean(sessionId)) || (status === "cancel" && Boolean(sessionId)),
  );
  const [paid, setPaid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orderIds, setOrderIds] = useState<string[]>([]);
  const [cancelDone, setCancelDone] = useState(false);

  useEffect(() => {
    if (status !== "success" || !sessionId) return;
    const checkoutSessionId = sessionId;

    let cancelled = false;

    async function verifyPayment(): Promise<void> {
      try {
        const result = await clientApi.getCheckoutStatus(checkoutSessionId);
        if (cancelled) return;
        setOrderIds(result.orderIds);
        setPaid(result.paid);
        if (!result.paid) {
          setError(
            "Le paiement est en cours de traitement. Actualisez la page dans quelques instants ou consultez vos commandes.",
          );
        }
      } catch {
        if (!cancelled) {
          setError(
            "Impossible de vérifier le paiement. Consultez l'historique de vos commandes.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void verifyPayment();
    return () => {
      cancelled = true;
    };
  }, [status, sessionId]);

  useEffect(() => {
    if (status !== "cancel" || !sessionId) return;
    const checkoutSessionId = sessionId;

    let cancelled = false;

    async function cancelOrders(): Promise<void> {
      try {
        await clientApi.cancelCheckoutSession(checkoutSessionId);
        if (cancelled) return;
        setCancelDone(true);
      } catch {
        if (!cancelled) {
          setError(
            "Impossible d'annuler automatiquement la commande. Vous pouvez l'annuler depuis le détail de commande.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void cancelOrders();
    return () => {
      cancelled = true;
    };
  }, [status, sessionId]);

  async function handleReorder(): Promise<void> {
    if (!sessionId) {
      navigate("/client/order/new");
      return;
    }
    try {
      const meta = await clientApi.getCheckoutStatus(sessionId);
      const orders = await Promise.all(meta.orderIds.map((id) => clientApi.getOrder(id)));
      const replay = buildReplayFromOrders(orders);
      navigate("/client/order/new", { state: replay ? { replay } : undefined });
    } catch {
      navigate("/client/order/new");
    }
  }

  if (status === "cancel") {
    return (
      <ClientLayout title="Paiement annulé">
        <Stack spacing={2} sx={{ py: 2 }}>
          {loading ? (
            <>
              <CircularProgress size={32} color="secondary" />
              <Typography variant="body2" color="text.secondary">
                Annulation de la commande…
              </Typography>
            </>
          ) : (
            <>
              <ErrorOutlineIcon color="warning" sx={{ fontSize: 48 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Paiement annulé
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {cancelDone
                  ? "Votre commande a été annulée. Vous pouvez la repasser avec les mêmes articles."
                  : "Le paiement n'a pas abouti. Vous pouvez recommencer votre commande."}
              </Typography>
              {error ? <Alert severity="warning">{error}</Alert> : null}
              {sessionId ? (
                <Button onClick={handleReorder} variant="contained" color="secondary">
                  Repasser la commande
                </Button>
              ) : (
                <Button
                  component={RouterLink}
                  to="/client/order/new"
                  variant="contained"
                  color="secondary"
                >
                  Nouvelle commande
                </Button>
              )}
              <Button component={RouterLink} to="/client/orders" variant="outlined">
                Voir mes commandes
              </Button>
            </>
          )}
        </Stack>
      </ClientLayout>
    );
  }

  if (status !== "success") {
    return (
      <ClientLayout title="Paiement">
        <Alert severity="warning">Lien de retour de paiement invalide.</Alert>
        <Button component={RouterLink} to="/client" sx={{ mt: 2 }}>
          Retour à l&apos;accueil
        </Button>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout title="Paiement">
      <Stack spacing={2} sx={{ py: 2 }} alignItems="flex-start">
        {loading ? (
          <>
            <CircularProgress size={32} color="secondary" />
            <Typography variant="body2" color="text.secondary">
              Vérification du paiement…
            </Typography>
          </>
        ) : paid ? (
          <>
            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 48 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Paiement confirmé
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {orderIds.length > 1
                ? `${orderIds.length} commandes ont été payées avec succès.`
                : "Votre commande a été payée avec succès."}
            </Typography>
            <Button component={RouterLink} to="/client/orders" variant="contained" color="secondary">
              Suivre mes commandes
            </Button>
          </>
        ) : (
          <>
            {error ? <Alert severity="info">{error}</Alert> : null}
            <Button component={RouterLink} to="/client/orders" variant="contained" color="secondary">
              Voir mes commandes
            </Button>
          </>
        )}
      </Stack>
    </ClientLayout>
  );
}
